'use client';
import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { Card, ImageWithSignedFallback } from '@ui';

export type ImageRotatorImage = { src: string; alt?: string; src2x?: string };

export interface ImageRotatorProps {
  images: ImageRotatorImage[];
  interval?: number; // ms
  width?: number | string;
  height?: number | string;
  className?: string;
  transition?: 'fade' | 'slide' | 'crossfade' | 'zoom' | 'flip' | 'kenburns' | 'dissolve' | 'none';
  transitionDuration?: number; // ms
  renderImage?: (img: ImageRotatorImage, idx: number) => React.ReactNode;
}

function slideKey(img: ImageRotatorImage, i: number) {
  return `${img.src}${img.src2x ?? ''}-${i}`;
}

const ImageRotator: React.FC<ImageRotatorProps> = ({
  images,
  interval = 3000,
  width = 300,
  height = 200,
  className,
  transition = 'fade',
  transitionDuration = 500,
  renderImage,
}) => {
  const [index, setIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState<number | null>(null);
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const TRANSITION_MS = Math.max(1, transitionDuration || 500);

  const isSlide = transition === 'slide';
  const realLen = images.length;

  useEffect(() => {
    if (realLen < 2) return;
    const timer = setInterval(() => {
      setIndex((i) => {
        if (isSlide) {
          if (direction === 'forward') {
            if (i + 1 >= realLen) {
              setDirection('backward');
              setPrevIndex(i);
              return i - 1;
            }
            setPrevIndex(i);
            return i + 1;
          } else {
            if (i - 1 < 0) {
              setDirection('forward');
              setPrevIndex(i);
              return i + 1;
            }
            setPrevIndex(i);
            return i - 1;
          }
        }
        setPrevIndex(i);
        return (i + 1) % realLen;
      });
    }, interval);
    return () => clearInterval(timer);
  }, [realLen, interval, isSlide, direction]);

  useEffect(() => {
    if (prevIndex === null) return;
    const t = setTimeout(() => setPrevIndex(null), TRANSITION_MS);
    return () => clearTimeout(t);
  }, [prevIndex, TRANSITION_MS]);

  if (!images.length) return null;

  const defaultWidth = typeof width === 'number' ? width : 300;
  const defaultHeight = typeof height === 'number' ? height : Math.round((defaultWidth * 3) / 4);

  const wrapperStyle: React.CSSProperties = {
    width: '100%',
    height: typeof height === 'number' ? height : 'auto',
    position: 'relative',
    overflow: 'hidden',
    ...(typeof height === 'string' && (height === 'auto' || height.endsWith('%') || height === '')
      ? ({ aspectRatio: '4 / 3', minHeight: '10rem' } as React.CSSProperties)
      : {}),
    ...({
      ['--image-rotator-duration']: `${TRANSITION_MS}ms`,
    } as React.CSSProperties),
  };

  const renderDefaultSlide = (img: ImageRotatorImage, i: number, slideOpacityStyle: React.CSSProperties) => {
    if (typeof renderImage === 'function') {
      return renderImage(img, i);
    }

    const alt = img.alt || `Image ${i + 1}`;

    if (img.src2x) {
      return (
        <img
          src={img.src}
          srcSet={`${img.src} 1x, ${img.src2x} 2x`}
          alt={alt}
          width={defaultWidth}
          height={defaultHeight}
          style={slideOpacityStyle}
          decoding='async'
          fetchPriority={i === index ? 'high' : 'auto'}
        />
      );
    }

    if (typeof img.src === 'string' && !img.src.startsWith('http') && !img.src.startsWith('/')) {
      return (
        <ImageWithSignedFallback
          src={img.src}
          alt={alt}
          width={defaultWidth}
          height={defaultHeight}
          style={slideOpacityStyle}
        />
      );
    }

    return (
      <Image
        src={img.src}
        alt={alt}
        width={defaultWidth}
        height={defaultHeight}
        style={slideOpacityStyle}
        sizes='100vw'
        priority={i === index}
        {...(typeof img.src === 'string' && img.src.startsWith('http') ? { unoptimized: true } : {})}
      />
    );
  };

  return (
    <Card className={className} style={{ width, height }}>
      {isSlide ? (
        <div style={wrapperStyle}>
          <div
            style={{
              display: 'flex',
              transition: `transform ${TRANSITION_MS}ms`,
              transform: `translateX(-${index * 100}%)`,
              height: '100%',
            }}
          >
            {images.map((img, i) => (
              <div key={slideKey(img, i)} style={{ flex: '0 0 100%' }}>
                {renderDefaultSlide(img, i, {
                  transition: `opacity ${TRANSITION_MS}ms`,
                  width: '100%',
                  height: '100%',
                })}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className={`image-rotator image-rotator--${transition}`} style={wrapperStyle}>
          {images.map((img, i) => (
            <div
              key={slideKey(img, i)}
              className={`slide ${i === index ? 'is-active' : ''} ${i === prevIndex ? 'is-prev' : ''}`}
              style={{ position: 'absolute', inset: 0 }}
            >
              {renderDefaultSlide(img, i, { width: '100%', height: '100%' })}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default ImageRotator;
